import { GiftDeliveryZonePolicy } from '@shared/api/giftItemBrand/giftItemBrand.interface';
export interface DeliveryPolicyForm {
  enabled: boolean;
  unlistedZone: 'reject' | 'allow';
  fee: string;
  freeOverAmount: string;
  rules: { sidos: string; fee: string; freeOverAmount: string }[];
}

/** 서버 매칭 키(normalizeSido 결과)와 같은 시도 약칭 17개 — 예외 요금은 이 값으로만 저장한다 */
export const SIDO_OPTIONS = [
  '서울',
  '경기',
  '인천',
  '부산',
  '대구',
  '광주',
  '대전',
  '울산',
  '세종',
  '강원',
  '충북',
  '충남',
  '전북',
  '전남',
  '경북',
  '경남',
  '제주',
] as const;

const SIDO_ALIASES: Record<string, string> = {
  경기도: '경기',
  충청북도: '충북',
  충청남도: '충남',
  전라북도: '전북',
  전라남도: '전남',
  경상북도: '경북',
  경상남도: '경남',
};
const SIDO_SUFFIX = /(특별자치시|특별자치도|광역시|특별시|도|시)$/;

/**
 * 손으로 적힌 시도 표기를 약칭으로 맞춘다(`서울특별시`·`서울시`→`서울`).
 * 서버 normalizeSido 는 `서울시`를 못 맞추므로 저장 전에 여기서 약칭으로 바꿔 둔다.
 * 17개 시도로 못 맞추면 null.
 */
export function canonicalSido(raw: string): string | null {
  const s = raw.trim();
  const key = SIDO_ALIASES[s] ?? s.replace(SIDO_SUFFIX, '');
  return (SIDO_OPTIONS as readonly string[]).includes(key) ? key : null;
}

export const parseSidos = (sidos: string): string[] =>
  Array.from(
    new Set(
      sidos
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => canonicalSido(s) ?? s),
    ),
  );

export const policyToForm = (policy?: GiftDeliveryZonePolicy): DeliveryPolicyForm => ({
  enabled: policy?.enabled ?? false,
  unlistedZone: policy?.unlistedZone ?? 'reject',
  fee: policy?.defaultBaseFee?.fee?.toString() ?? '',
  freeOverAmount: policy?.defaultBaseFee?.freeOverAmount?.toString() ?? '',
  rules: (policy?.baseFeeRules ?? []).map((r) => ({
    sidos: parseSidos((r.sidoIn ?? []).join(',')).join(', '),
    fee: String(r.fee),
    freeOverAmount: String(r.freeOverAmount),
  })),
});
export function formToPolicy(
  form: DeliveryPolicyForm,
  existing?: GiftDeliveryZonePolicy,
): GiftDeliveryZonePolicy | undefined {
  if (!form.enabled) return existing ? { ...existing, enabled: false } : undefined;
  const money = (v: string) => {
    if (!v.trim() || !Number.isSafeInteger(Number(v)) || Number(v) < 0)
      throw new Error('기본배송비와 면제금액은 0 이상의 정수로 입력해 주세요.');
    return Number(v);
  };
  return {
    ...existing,
    enabled: true,
    unlistedZone: form.unlistedZone,
    defaultBaseFee: { fee: money(form.fee), freeOverAmount: money(form.freeOverAmount) },
    baseFeeRules: form.rules.map((r) => {
      const sidoIn = parseSidos(r.sidos);
      if (!sidoIn.length) throw new Error('예외 요금에 적용할 시도를 선택해 주세요.');
      const unknown = sidoIn.filter((s) => canonicalSido(s) === null);
      if (unknown.length)
        throw new Error(
          `인식할 수 없는 시도가 있습니다: ${unknown.join(', ')} — 삭제 후 목록에서 선택해 주세요.`,
        );
      return { sidoIn, fee: money(r.fee), freeOverAmount: money(r.freeOverAmount) };
    }),
  };
}
